import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { ProductListBaseAddRequestDto } from '@epicuro-next/data/engagement/engagement-models';
import { List, MediaArgs } from '@epicuro-next/models/engagement';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import { go } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  first,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import * as actions from '../../actions';
import { ICreateListWithProducts } from '../../models';
import { selectListOldBulkAddStyleIdInput } from '../../selectors';
import { generateListName } from '../../utils/list-name-generator';

/**
 * List create Effects
 */
@Injectable()
export class CreateEffects {
  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
  );
  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  public createList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createList),
      concatLatestFrom(() => [this.accountId$, this.siteId$]),
      switchMap(([{ list, media }, accountId, siteId]) => {
        const payload = {
          ...list,
          accountId,
          siteId,
        };

        if (media) {
          payload.media = [media as MediaArgs];
        }

        return this.productListApi
          .create(payload as ProductListBaseAddRequestDto)
          .pipe(
            map((item: List) => actions.createListSuccess({ list: item })),
            catchError((error) => of(actions.createListError({ error }))),
          );
      }),
    ),
  );

  public createListWithProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createListWithProducts),
      concatLatestFrom(() => [
        this.accountId$,
        this.siteId$,
        this.store.pipe(select(selectListOldBulkAddStyleIdInput)),
      ]),
      switchMap(([params, accountId, siteId, stylesOnly]) =>
        this.createListWithProducts(params, accountId, siteId, stylesOnly).pipe(
          concatMap((listItem: List) => [
            actions.createListSuccess({
              list: listItem,
            }),
            actions.setOpenedList({ id: listItem.id }),
            actions.resetOpenedListProducts(),
          ]),
          catchError((error) => of(actions.createListError({ error }))),
        ),
      ),
    ),
  );

  public createListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createListSuccess, actions.createCommercialListSuccess),
      map(({ list }) =>
        go({
          commands: [
            APP_PRIVATE_PATHS.browse.path,
            APP_PRIVATE_PATHS.productList.path,
            list.id,
          ],
        }),
      ),
    ),
  );

  public generateList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.generateList),
      concatLatestFrom(() => [this.accountId$, this.siteId$]),
      switchMap(([{ products }, accountId, siteId]) => {
        return this.productListApi
          .create({
            name: generateListName(),
            accountId,
            siteId,
          })
          .pipe(
            map((list: List) =>
              products.length > 0
                ? actions.addProductsToList({ list, products })
                : actions.saveAllItemsInList({ list }),
            ),
            catchError((error) => of(actions.createListError({ error }))),
          );
      }),
    ),
  );

  public createListError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.createListError),
        tap((action) => {
          this.loggerService.exception({
            error: new Error(action.error),
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  public showSnackBarCreatedProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createListSuccess),
      map(({ list }) =>
        showSnackBarMessage({
          message: $localize`:@@CreateProductListSnackMessage:${
            list.name ?? 'List'
          } created.`,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  public createCommercialListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createCommercialListSuccess),
      map(({ list }) =>
        showSnackBarMessage({
          message: $localize`:@@CreateCommercialProductListSnackMessage:${
            list.name ?? 'Commercial List'
          } created.`,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private productListApi: ProductListApiService,
    private loggerService: LoggerService,
    private store: Store,
  ) {}

  private createListWithProducts(
    { list, media, products }: ICreateListWithProducts,
    accountId: string,
    siteId: string,
    // tslint:disable-next-line:bool-param-default
    stylesOnly?: boolean,
  ) {
    return this.productListApi
      .create({
        ...list,
        media: [media as MediaArgs],
        productReferences: products,
        accountId,
        siteId,
        stylesOnly,
      })
      .pipe(
        first(() => products.length > 0),
        map((createdList: List) => {
          return createdList;
        }),
      );
  }
}
